import {useCallback, useEffect, useState} from 'react';
import {Spinner} from '~/common/components/Loader/Spinner';
import {LabelPremium} from '~/GalleryPB/components/Template/components/QuickView/components/LabelPremium/LabelPremium';
import {PremiumAnimationB} from '~/GalleryPB/components/Template/components/QuickView/components/QuickViewAnimation/PremiumAnimationB';
import {type Template, type TemplateSet, VariantType} from '~/GalleryPB/types/template';
import {pickSrc} from '~/GalleryPB/utils';
import {Video} from '~library/atoms/Video';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';

import './QuickViewAnimation.sass';

type QuickViewAnimationProps = {
  template: Template;
  variant: VariantType;
};

const MEDIA_INDEX_MOBILE = 2; // Mobile media index
const MEDIA_INDEX_DESKTOP = 1; // Desktop media index

const NOT_REGULAR_SHAPES = ['diecut', 'cutout', 'die cut', 'cut out'];

const simpleTemplateCache = new Map();

export const QuickViewAnimation = ({template, variant}: QuickViewAnimationProps) => {
  const [templateSet, setTemplateSet] = useState<TemplateSet | undefined>();
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const isMediumAndUp = useIsMediumAndUp();
  const isPremiumVariant = variant === VariantType.PREMIUM;
  const isPremiumTemplate = template.premium as boolean;
  const isNotRegularTemplate = template.keywords.some((keyword) =>
    NOT_REGULAR_SHAPES.includes(keyword)
  );

  const [premBackground, setPremBackground] = useState<string | undefined>();

  const fetchTemplateData = useCallback(async () => {
    if (template.templateId) {
      const mediaIndex = isMediumAndUp ? MEDIA_INDEX_DESKTOP : MEDIA_INDEX_MOBILE;

      if (!simpleTemplateCache.has(template.templateId)) {
        try {
          const res = await evite.fetch(
            `/ajax/gallery/template/${template.templateId}?include_assets_data`,
            {
              method: 'GET',
            }
          );

          const data = await res.json();

          simpleTemplateCache.set(template.templateId, data);

          setTemplateSet(data);
          const premBg = data?.backgrounds?.[0]?.media?.[mediaIndex]?.url;
          setPremBackground(premBg);
        } catch (error) {
          evite.error('Error fetching template data:', error);
        }
      } else {
        const data = simpleTemplateCache.get(template.templateId);
        setTemplateSet(data);
        const premBg = data?.backgrounds?.[0]?.media?.[mediaIndex]?.url;
        setPremBackground(premBg);
      }
    }
  }, [isMediumAndUp, template.templateId]);

  useEffect(() => {
    fetchTemplateData();
  }, [fetchTemplateData]);

  const onAssetsLoaded = useCallback(() => setAssetsLoaded(true), []);

  const loadPremiumBackground = () =>
    premBackground?.endsWith('.mp4') ? (
      <Video src={premBackground} className="quick-view-animation__wrapper__background" />
    ) : (
      <div
        className="quick-view-animation__wrapper__background"
        style={{background: `url(${premBackground})`}}
      />
    );

  return (
    <div className="quick-view-animation__wrapper">
      {isPremiumVariant && premBackground ? loadPremiumBackground() : null}
      {isPremiumVariant && !isPremiumTemplate && (
        <LabelPremium className="quick-view-animation__wrapper__label-premium" />
      )}
      {!assetsLoaded && (
        <div className="quick-view-animation__wrapper__loading">
          <Spinner />
        </div>
      )}
      <PremiumAnimationB
        template={template}
        templateSrc={pickSrc(template, isMediumAndUp, false)}
        skipAnimation={variant === VariantType.FREE}
        liner={templateSet?.liners?.[0]?.media?.[1]?.url}
        envelopeOpen={templateSet?.envelopes?.[0]?.blank}
        envelopeClosedBack={templateSet?.envelopes?.[0]?.flap_down_image}
        onAssetsLoaded={onAssetsLoaded}
        style={{
          opacity: assetsLoaded ? 1 : 0,
        }}
        noShadowOnCard={isNotRegularTemplate}
      />
    </div>
  );
};
